import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';

import Modal from './Modal';
import Button from '../Forms/Button/Button';
import Message from '../Messages/Message';
import AddressLocationPicker from '../../Geo/AddressLocationPicker';
import { addDemandToDbAPI } from '../../../Store/Actions/demandActions';
import { normalizeRegionCode, regionOptions } from '../../../Config/franceRegions';
import { useI18n } from '../../../i18n/I18nProvider';

const MAX_IMAGES = 6;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const PostModal = ({ showModal, handleClick, firebase, postDemand, loading, error }) => {
  const { pick } = useI18n();
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState(firebase.profile.phone_number || '');
  const [address, setAddress] = useState('');
  const [exactLocation, setExactLocation] = useState(null);
  const [demandType, setDemandType] = useState('');
  const [demandRegion, setDemandRegion] = useState('');
  const [fileError, setFileError] = useState('');
  const [publicPreviewConsent, setPublicPreviewConsent] = useState(false);
  const imagesRef = useRef(images);
  imagesRef.current = images;

  useEffect(() => {
    if (!phoneNumber && firebase.profile.phone_number) {
      setPhoneNumber(String(firebase.profile.phone_number));
    }
  }, [firebase.profile.phone_number, phoneNumber]);

  useEffect(() => () => {
    imagesRef.current.forEach((image) => URL.revokeObjectURL(image.preview));
  }, []);

  const resetForm = () => {
    images.forEach((image) => URL.revokeObjectURL(image.preview));
    setDescription('');
    setImages([]);
    setAddress('');
    setExactLocation(null);
    setDemandType('');
    setDemandRegion('');
    setFileError('');
    setPublicPreviewConsent(false);
  };

  const close = () => {
    if (loading) return;
    resetForm();
    handleClick?.();
  };

  const handleLocationChange = (nextLocation) => {
    setExactLocation(nextLocation);
    const detectedRegion = normalizeRegionCode(nextLocation?.region || '');
    if (detectedRegion && regionOptions.some((region) => region.value === detectedRegion)) {
      setDemandRegion(detectedRegion);
    }
  };

  const handleImageChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    setFileError('');

    const remainingSlots = MAX_IMAGES - images.length;
    if (selectedFiles.length > remainingSlots) {
      setFileError(pick(`Vous pouvez ajouter jusqu’à ${MAX_IMAGES} photos.`, `You can add up to ${MAX_IMAGES} photos.`));
      return;
    }

    const invalid = selectedFiles.find(
      (file) => !ACCEPTED_TYPES.includes(file.type) || file.size > MAX_IMAGE_BYTES,
    );
    if (invalid) {
      setFileError(pick('Utilisez des images JPG, PNG ou WebP de moins de 8 Mo.', 'Use JPG, PNG or WebP images under 8 MB.'));
      return;
    }

    setImages((current) => [
      ...current,
      ...selectedFiles.map((file) => ({
        id: `${file.name}-${file.lastModified}-${Math.random()}`,
        file,
        preview: URL.createObjectURL(file),
      })),
    ]);
  };

  const removeImage = (id) => {
    setImages((current) => {
      const removed = current.find((image) => image.id === id);
      if (removed) URL.revokeObjectURL(removed.preview);
      return current.filter((image) => image.id !== id);
    });
  };

  const isValid = useMemo(() => (
    description.trim().length >= 10 &&
    phoneNumber.trim().length >= 8 &&
    address.trim().length >= 5 &&
    Boolean(demandType) &&
    Boolean(demandRegion)
  ), [description, phoneNumber, address, demandType, demandRegion]);

  const submit = async (event) => {
    event.preventDefault();
    if (!isValid || loading) return;

    const demandId = await postDemand({
      clientId: firebase.auth.uid,
      demandImages: images.map((image) => image.file),
      userFirstName: firebase.profile.firstName,
      userLastName: firebase.profile.lastName,
      userEmail: firebase.auth.email || firebase.profile.email,
      clientProfileImage: firebase.profile.profile_image || '',
      clientTrustScore: Number(firebase.profile.trust_score || firebase.profile.trust?.score || 0),
      clientAverageRating: Number(firebase.profile.average_rating || firebase.profile.client_rating || firebase.profile.trust?.average_rating || 0),
      clientVerifiedProjects: Number(firebase.profile.verified_projects || firebase.profile.trust?.verified_projects || 0),
      clientAddress: firebase.profile.address || '',
      description: description.trim(),
      demandType,
      contactNumber: phoneNumber.trim(),
      address: address.trim(),
      exactLocation,
      demandRegion,
      publicPreviewConsent,
    });

    if (demandId) close();
  };

  return (
    <Modal opened={showModal === 'open'} close={close}>
      <Header>
        <div>
          <Eyebrow>{pick('Nouvelle demande', 'New request')}</Eyebrow>
          <h2>{pick('Décrivez votre petit boulot', 'Describe the work you need')}</h2>
          <p>{pick('Des informations précises et quelques photos aideront les artisans à vous répondre rapidement.', 'Clear information and a few photos will help artisans respond quickly.')}</p>
        </div>
        <CloseButton type="button" onClick={close} aria-label={pick('Fermer', 'Close')} disabled={loading}>
          <CloseIcon />
        </CloseButton>
      </Header>

      <Form onSubmit={submit}>
        <FieldGroup>
          <Label htmlFor="demand-description">{pick('Description du besoin *', 'Describe your need *')}</Label>
          <Textarea
            id="demand-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={pick('Exemple : remplacer une fenêtre de 120 × 100 cm au premier étage…', 'Example: replace a 120 × 100 cm first-floor window…')}
            minLength={10}
            required
          />
          <Hint>{description.trim().length}/10 {pick('caractères minimum', 'characters minimum')}</Hint>
        </FieldGroup>

        <TwoColumns>
          <FieldGroup>
            <Label htmlFor="demand-type">{pick('Type de prestation *', 'Type of service *')}</Label>
            <Select id="demand-type" value={demandType} onChange={(event) => setDemandType(event.target.value)} required>
              <option value="">{pick('Choisir une spécialité', 'Choose a speciality')}</option>
              <option value="window">{pick('Menuiserie / miroiterie', 'Joinery / glazing')}</option>
              <option value="electricity">{pick('Électricité', 'Electrical work')}</option>
            </Select>
          </FieldGroup>
          <FieldGroup>
            <Label htmlFor="demand-region">{pick('Région *', 'Region *')}</Label>
            <Select id="demand-region" value={demandRegion} onChange={(event) => setDemandRegion(event.target.value)} required>
              <option value="">{pick('Choisir une région', 'Choose a region')}</option>
              {regionOptions.map((region) => (
                <option key={region.value} value={region.value}>{region.label}</option>
              ))}
            </Select>
          </FieldGroup>
        </TwoColumns>

        <FieldGroup>
          <Label htmlFor="demand-phone">{pick('Téléphone *', 'Phone *')}</Label>
          <Input id="demand-phone" type="tel" inputMode="tel" autoComplete="tel" value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} required />
        </FieldGroup>

        <LocationSection>
          <AddressLocationPicker
            id="demand-address"
            label={pick('Adresse exacte du chantier', 'Exact project address')}
            value={address}
            location={exactLocation}
            onAddressChange={setAddress}
            onLocationChange={handleLocationChange}
            required
            placeholder={pick('Commencez à saisir l’adresse du chantier', 'Start typing the project address')}
            showMap
          />
          <LocationPrivacy>
            {pick('Les artisans qui explorent les projets voient uniquement une localisation approximative. L’adresse exacte est débloquée après acceptation de leur proposition de visite.', 'Artisans browsing projects only see an approximate location. The exact address is revealed after you accept their visit proposal.')}
          </LocationPrivacy>
          {exactLocation && (
            <LocationConfirmed>
              {pick('Adresse confirmée · la région a été présélectionnée automatiquement lorsque Google l’a reconnue.', 'Address confirmed · the region was preselected automatically when Google recognised it.')}
            </LocationConfirmed>
          )}
        </LocationSection>

        <FieldGroup>
          <Label>{pick('Photos du chantier', 'Project photos')} <Optional>{pick('facultatif', 'optional')} — {images.length}/{MAX_IMAGES}</Optional></Label>
          <UploadArea>
            <UploadButton htmlFor="demand-images">
              <AddPhotoAlternateOutlinedIcon />
              {pick('Ajouter des photos', 'Add photos')}
            </UploadButton>
            <input id="demand-images" type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={handleImageChange} hidden />
            <Hint>{pick('JPG, PNG ou WebP, 8 Mo maximum par image.', 'JPG, PNG or WebP, maximum 8 MB per image.')}</Hint>
          </UploadArea>
          {images.length > 0 && (
            <PreviewGrid>
              {images.map((image) => (
                <Preview key={image.id}>
                  <img src={image.preview} alt={pick('Aperçu du chantier', 'Project preview')} />
                  <button type="button" onClick={() => removeImage(image.id)} aria-label={pick('Retirer cette photo', 'Remove this photo')}><CloseIcon /></button>
                </Preview>
              ))}
            </PreviewGrid>
          )}
          <Message error show={Boolean(fileError)}>{fileError}</Message>
        </FieldGroup>


        <PublicPreviewOption>
          <input
            id="public-preview-consent"
            type="checkbox"
            checked={publicPreviewConsent}
            onChange={(event) => setPublicPreviewConsent(event.target.checked)}
          />
          <div>
            <label htmlFor="public-preview-consent">{pick('Afficher un aperçu anonymisé avant inscription', 'Show an anonymised public preview')}</label>
            <p>
              {pick('LePetitBoulot pourra présenter la spécialité, une photo, la date et une zone approximative. Votre nom, votre téléphone et l’adresse exacte ne seront jamais affichés publiquement.', 'LePetitBoulot may show the speciality, one photo, the date and an approximate area. Your name, phone number and exact address will never be shown publicly.')}
            </p>
          </div>
        </PublicPreviewOption>

        <Message error show={Boolean(error)}>{error}</Message>

        <Actions>
          <ActionStatus $ready={isValid}>
            <strong>{isValid ? pick('Votre demande est prête', 'Your request is ready') : pick('Complétez les champs obligatoires', 'Complete the required fields')}</strong>
            <span>{isValid ? pick('Vous pourrez encore suivre et enrichir le projet après publication.', 'You can continue to follow and update the project after publishing.') : pick('Description, spécialité, région, téléphone et adresse sont nécessaires.', 'Description, speciality, region, phone and address are required.')}</span>
          </ActionStatus>
          <ActionButtons>
            <Button contain type="button" onClick={close} disabled={loading}>{pick('Annuler', 'Cancel')}</Button>
            <Button contain type="submit" disabled={!isValid || loading} loading={loading ? pick('Publication…', 'Publishing…') : null}>
              {pick('Publier la demande', 'Publish request')}
            </Button>
          </ActionButtons>
        </Actions>
      </Form>
    </Modal>
  );
};



const PublicPreviewOption = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: start;
  gap: .75rem;
  padding: .9rem 1rem;
  border: 1px solid #dfd6c8;
  border-radius: .9rem;
  background: #fbf8f2;

  input {
    width: 1.1rem;
    height: 1.1rem;
    margin-top: .12rem;
    accent-color: #111;
  }

  div { min-width: 0; }
  label { color: #111; font-size: .84rem; font-weight: 850; cursor: pointer; }
  p { margin: .28rem 0 0; color: #6f685f; font-size: .74rem; line-height: 1.5; }
`;

const LocationSection = styled.section`
  display: grid;
  gap: .75rem;
  padding: 1rem;
  border: 1px solid #dbe5eb;
  border-radius: 1rem;
  background: #f8fbfc;
`;
const LocationPrivacy = styled.p`
  margin: 0;
  padding: .65rem .75rem;
  border-left: 3px solid var(--color-main);
  border-radius: 0 .65rem .65rem 0;
  background: #eef5f8;
  color: #536b7b;
  font-size: .75rem;
  line-height: 1.5;
`;

const LocationConfirmed = styled.div`
  padding: .65rem .75rem;
  border: 1px solid #b9dfc8;
  border-radius: .75rem;
  background: #edf8f1;
  color: #22643f;
  font-size: .75rem;
  font-weight: 750;
  line-height: 1.45;
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;

  h2 { margin: 0; color: var(--color-mainDark); font-size: clamp(1.5rem, 4vw, 2rem); }
  p { margin: 0.55rem 0 0; color: var(--color-textMuted); line-height: 1.55; }
`;

const Eyebrow = styled.div`
  margin-bottom: 0.35rem;
  color: var(--color-main);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  flex: 0 0 auto;
  width: 2.6rem;
  height: 2.6rem;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 0.75rem;
  background: #f1f4f6;
  color: var(--color-text);
  cursor: pointer;
`;

const Form = styled.form`
  display: grid;
  gap: 1rem;
`;

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  @media (max-width: 620px) { grid-template-columns: 1fr; }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.42rem;
`;

const Label = styled.label`
  color: var(--color-mainDark);
  font-size: 0.86rem;
  font-weight: 800;
`;

const Optional = styled.span`
  color: var(--color-textMuted);
  font-weight: 600;
`;

const controlStyles = `
  width: 100%;
  min-height: 3rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 0.75rem;
  background: #fff;
  color: var(--color-text);
  font: inherit;
  &:focus { border-color: var(--color-main); box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.1); outline: none; }
`;

const Input = styled.input`${controlStyles}`;
const Select = styled.select`${controlStyles}`;
const Textarea = styled.textarea`
  ${controlStyles}
  min-height: 8rem;
  resize: vertical;
  line-height: 1.55;
`;

const Hint = styled.span`
  color: var(--color-textMuted);
  font-size: 0.76rem;
`;

const UploadArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.7rem;
`;

const UploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.8rem;
  border: 1px dashed rgba(0, 0, 0, 0.3);
  border-radius: 0.75rem;
  background: #faf8f4;
  color: var(--color-mainDark);
  font-size: 0.84rem;
  font-weight: 800;
  cursor: pointer;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(95px, 1fr));
  gap: 0.65rem;
`;

const Preview = styled.div`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 0.75rem;
  background: #eef1f4;

  img { width: 100%; height: 100%; object-fit: cover; }
  button {
    position: absolute; top: 0.35rem; right: 0.35rem; width: 1.8rem; height: 1.8rem;
    display: grid; place-items: center; border: 0; border-radius: 50%; background: rgba(7, 25, 42, 0.78); color: #fff; cursor: pointer;
  }
  svg { font-size: 1rem; }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: .4rem;
  padding-top: .85rem;
  border-top: 1px solid #ece5da;
  background: #fff;

  @media (max-width: 640px) {
    position: sticky;
    z-index: 8;
    bottom: 0;
    display: grid;
    grid-template-columns: 1fr;
    margin: .5rem -1rem calc(-1.25rem - env(safe-area-inset-bottom));
    padding: .8rem 1rem calc(.8rem + env(safe-area-inset-bottom));
    border-top: 1px solid #ded5c8;
    box-shadow: 0 -1rem 2rem rgba(17,17,17,.08);
  }
`;

const ActionStatus = styled.div`
  min-width: 0;
  display: grid;
  gap: .15rem;
  color: ${({ $ready }) => ($ready ? '#22643f' : '#725515')};
  strong { font-size: .82rem; }
  span { color: #746d64; font-size: .72rem; line-height: 1.4; }

  @media(max-width:640px) { display: none; }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: .7rem;
  @media(max-width:640px) {
    display: grid;
    grid-template-columns: 1fr 1.25fr;
    > * { width: 100%; }
  }
`;

const mapStateToProps = ({ firebase, demands }) => ({
  firebase,
  loading: demands.loading,
  error: demands.error,
});

const mapDispatchToProps = (dispatch) => ({
  postDemand: (payload) => dispatch(addDemandToDbAPI(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PostModal);
